import type { DrivetrainEvent, DrivetrainState } from '../drivetrain/VirtualDrivetrain';
import type { OneShots } from './EngineSampleManager';

/**
 * GearShiftProcessor — plays the one-shot layer: shift clacks, downshift
 * clacks (the blip itself comes from the drivetrain raising throttle),
 * limiter bursts and lift-off backfires.
 */
export class GearShiftProcessor {
  readonly output: GainNode;
  private lastBackfire = 0;
  // limiter burst: a LONG recording (≈2.5 s of engine pinned at the limiter).
  // Only start it once the limiter has been held for a moment (an upshift that
  // brushes the limiter for one frame must NOT fire it) and fade it the instant
  // the limiter is left — otherwise it keeps playing over the next gear and
  // reads as a doubled engine / echo.
  private limiterHeld = 0;
  // LAUNCH CONTROL: the same machinery as the limiter, because it is the same shape of thing — a long
  // recording of the engine PINNED, held while a condition lasts and gone the moment it ends. It is
  // driven from AudioEngine rather than from DrivetrainState: standing on both pedals is an audio
  // reading of the drive, not a gear the drivetrain is in.
  private launchSrc: AudioBufferSourceNode | null = null;
  private launchGain: GainNode | null = null;
  private launchSrc2: AudioBufferSourceNode | null = null;
  private launchGain2: GainNode | null = null;
  private launchSrc3: AudioBufferSourceNode | null = null;
  private launchGain3: GainNode | null = null;
  private cabinBus: AudioNode | null = null;
  private limiterSrc: AudioBufferSourceNode | null = null;
  private limiterGain: GainNode | null = null;
  private limiterSrc2: AudioBufferSourceNode | null = null;
  private limiterGain2: GainNode | null = null;
  private limiterSrc3: AudioBufferSourceNode | null = null;
  private limiterGain3: GainNode | null = null;
  private extraBus: AudioNode | null = null;

  constructor(
    private readonly ctx: AudioContext,
    private readonly shots: OneShots,
  ) {
    this.output = ctx.createGain();
    this.output.gain.value = 0.9;
  }

  handle(e: DrivetrainEvent, state: DrivetrainState): void {
    switch (e.type) {
      case 'shiftUp': {
        this.stopLimiter();
        // real recorded gearchange: pick the clip recorded closest to the current rpm and rate-shift it there
        const i = nearest(this.shots.shiftUpRpm, e.rpm);
        const rec = this.shots.shiftUpRpm[i] || 0;
        const rate = rec ? Math.min(1.12, Math.max(0.88, e.rpm / rec)) : 1 + (Math.random() - 0.5) * 0.06;
        this.play(this.shots.shiftUp[i] ?? null, rec ? 0.35 : 0.55, rate); // clack only (HP'd): sits under the voice
        break;
      }
      case 'shiftDown': {
        this.stopLimiter();
        const i = nearest(this.shots.shiftDownRpm, e.rpm);
        const rec = this.shots.shiftDownRpm[i] || 0;
        const rate = rec ? Math.min(1.12, Math.max(0.88, e.rpm / rec)) : 0.97 + (Math.random() - 0.5) * 0.06;
        this.play(this.shots.shiftDown[i] ?? null, rec ? 0.13 : 0.2, rate); // clack WAY down (Marc: the downshift clack read as unrealistic) — the voice's overrun carries the blip (Run_05)
        break;
      }
      case 'limiter':
        // handled continuously in update(): needs the limiter to persist
        break;
      case 'backfire': {
        const now = this.ctx.currentTime;
        if (now - this.lastBackfire < 0.4) return;
        this.lastBackfire = now;
        const g = 0.35 + 0.5 * state.rpmNorm;
        this.play(pick(this.shots.backfire), g, 0.9 + Math.random() * 0.25);
        break;
      }
      default:
        break;
    }
  }

  /** Ignition one-shots (ENG START / STOP). Returns the clip length in seconds (0 if missing). */
  private fsIdx = 0;

  playNamed(name: 'starter' | 'startup' | 'shutoff' | 'falsestart' | 'doorOpen' | 'doorShut', gain = 1, settle?: { at: number; gain: number; over: number }): { duration: number; stop: (fadeS?: number) => void } {
    let b = this.shots[name];
    if (name === 'falsestart' && this.shots.falsestarts.length) {
      // three real failed starts — take turns (Marc: 'hay 3, pues ves turnando')
      b = this.shots.falsestarts[this.fsIdx % this.shots.falsestarts.length];
      this.fsIdx++;
    }
    if (!b) return { duration: 0, stop: () => undefined };
    const h = this.play(b, gain, 1, settle);
    return { duration: b.duration, stop: h };
  }

  /** How long a one-shot is, without playing it — the ignition needs it to place the crossfade. */
  lengthOf(name: 'starter' | 'startup' | 'shutoff' | 'falsestart' | 'doorOpen' | 'doorShut'): number {
    return this.shots[name]?.duration ?? 0;
  }

  /**
   * Where the CABIN copy of the launch bed goes. It is the overlay bus rather than this processor's
   * own output, so raising the CABIN fader moves launch control inside the car along with everything
   * else. Set after the overlay is built, which is later than this processor is constructed.
   */
  setCabinBus(node: AudioNode | null): void { this.cabinBus = node; }

  /** and the fourth fader's, for the same reason */
  setExtraBus(node: AudioNode | null): void { this.extraBus = node; }

  /** Launch control bed: on while both pedals are down at a standstill, rate-matched to the held revs. */
  setLaunch(on: boolean, rpm: number): void {
    const rec = this.shots.launchRpm || 4000;
    const rate = () => Math.min(1.3, Math.max(0.75, rpm / rec));
    // one bed per microphone the mod recorded it from, each on its own bus
    const beds: [AudioBuffer | null, AudioNode | null, 'a' | 'b' | 'c'][] = [
      [this.shots.launch, this.output, 'a'],
      [this.shots.launchCabin, this.cabinBus, 'b'],
      [this.shots.launchExtra, this.extraBus, 'c'],
    ];
    for (const [buf, dest, slot] of beds) {
      const src0 = slot === 'a' ? this.launchSrc : slot === 'b' ? this.launchSrc2 : this.launchSrc3;
      const g0 = slot === 'a' ? this.launchGain : slot === 'b' ? this.launchGain2 : this.launchGain3;
      if (!buf || !dest) continue;
      if (on && !src0) {
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        src.playbackRate.value = rate();
        const g = this.ctx.createGain();
        g.gain.value = 0;
        g.gain.setTargetAtTime(0.7, this.ctx.currentTime, 0.06);
        src.connect(g).connect(dest);
        src.start(0, Math.random() * Math.max(0, buf.duration - 2));
        src.onended = () => {
          if (slot === 'a' && this.launchSrc === src) { this.launchSrc = null; this.launchGain = null; }
          if (slot === 'b' && this.launchSrc2 === src) { this.launchSrc2 = null; this.launchGain2 = null; }
          if (slot === 'c' && this.launchSrc3 === src) { this.launchSrc3 = null; this.launchGain3 = null; }
        };
        if (slot === 'a') { this.launchSrc = src; this.launchGain = g; }
        else if (slot === 'b') { this.launchSrc2 = src; this.launchGain2 = g; }
        else { this.launchSrc3 = src; this.launchGain3 = g; }
      } else if (on && src0 && rpm > 0) {
        src0.playbackRate.value = rate();
      } else if (!on && src0) {
        if (slot === 'a') { this.launchSrc = null; this.launchGain = null; }
        else if (slot === 'b') { this.launchSrc2 = null; this.launchGain2 = null; }
        else { this.launchSrc3 = null; this.launchGain3 = null; }
        if (g0) g0.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
        setTimeout(() => { try { src0.stop(); } catch { /* already gone */ } }, 260);
      }
    }
  }

  /** Per-frame: start the limiter burst only after it has been held, stop it when left. */
  update(state: DrivetrainState, dtS: number): void {
    if (state.limiter && !state.shifting) {
      this.limiterHeld += dtS;
      // the real bed was recorded ~5.8k (pit lane in 1st): rate-match to the current revs; above ~7.2k
      // (the 9450 rev limiter) it would pitch out of its world — the voice + chop carry it there
      const rec = this.shots.limiterRpm || 5800;
      const rate = state.rpm > 0 ? state.rpm / rec : 1;
      // one bed per microphone the bank recorded the cut from, each on its own bus, so the cabin
      // fader carries the limiter inside the car along with everything else
      const beds: [AudioBuffer | null, AudioNode | null, 'a' | 'b' | 'c'][] = [
        [this.shots.limiter, this.output, 'a'],
        [this.shots.limiterCabin, this.cabinBus, 'b'],
        [this.shots.limiterExtra, this.extraBus, 'c'],
      ];
      if (this.limiterHeld >= 0.12 && rate >= 0.8 && rate <= 1.25) {
        for (const [buf, dest, slot] of beds) {
          if (!buf || !dest) continue;
          if (slot === 'a' ? this.limiterSrc : slot === 'b' ? this.limiterSrc2 : this.limiterSrc3) continue;
          const src = this.ctx.createBufferSource();
          src.buffer = buf;
          src.loop = true;
          src.playbackRate.value = rate;
          src.onended = () => {
            if (slot === 'a' && this.limiterSrc === src) { this.limiterSrc = null; this.limiterGain = null; }
            if (slot === 'b' && this.limiterSrc2 === src) { this.limiterSrc2 = null; this.limiterGain2 = null; }
            if (slot === 'c' && this.limiterSrc3 === src) { this.limiterSrc3 = null; this.limiterGain3 = null; }
          };
          const g = this.ctx.createGain();
          g.gain.value = 0;
          g.gain.setTargetAtTime(0.65, this.ctx.currentTime, 0.05);
          src.connect(g).connect(dest);
          // a limiter bed can be a LONG pinned take or a tiny 0.2 s tick (Kunos): the offset maths
          // must never go negative or start() throws and the whole limiter stays silent
          src.start(0, Math.random() * Math.max(0, buf.duration - 1));
          if (slot === 'a') { this.limiterSrc = src; this.limiterGain = g; }
          else if (slot === 'b') { this.limiterSrc2 = src; this.limiterGain2 = g; }
          else { this.limiterSrc3 = src; this.limiterGain3 = g; }
        }
      }
      if (state.rpm > 0) {
        const r2 = Math.min(1.25, Math.max(0.8, state.rpm / rec));
        if (this.limiterSrc) this.limiterSrc.playbackRate.value = r2;
        if (this.limiterSrc2) this.limiterSrc2.playbackRate.value = r2;
        if (this.limiterSrc3) this.limiterSrc3.playbackRate.value = r2;
      }
    } else {
      this.limiterHeld = 0;
      this.stopLimiter();
    }
  }

  private stopLimiter(): void {
    this.stopOneLimiter(this.limiterSrc, this.limiterGain);
    this.stopOneLimiter(this.limiterSrc2, this.limiterGain2);
    this.stopOneLimiter(this.limiterSrc3, this.limiterGain3);
    this.limiterSrc = null; this.limiterGain = null;
    this.limiterSrc2 = null; this.limiterGain2 = null;
    this.limiterSrc3 = null; this.limiterGain3 = null;
  }

  private stopOneLimiter(src: AudioBufferSourceNode | null, g: GainNode | null): void {
    if (!src || !g) return;
    const now = this.ctx.currentTime;
    g.gain.cancelScheduledValues(now);
    g.gain.setTargetAtTime(0, now, 0.025);
    try {
      src.stop(now + 0.15);
    } catch {
      /* already stopped */
    }
    src.onended = () => {
      src.disconnect();
      g.disconnect();
    };
  }

  private play(buffer: AudioBuffer | null, gain: number, rate: number, settle?: { at: number; gain: number; over: number }): (fadeS?: number) => void {
    if (!buffer) return () => undefined;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    const g = this.ctx.createGain();
    // NO CLIP STARTS FROM A HARD EDGE (Marc, from Premiere: two takes butted together click, a short
    // crossfade and the click is gone). Five milliseconds from silence is below anything the ear can
    // place, and it takes the DC step out of a take whose first sample is not zero.
    const t0 = this.ctx.currentTime;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
    if (settle) {
      // e.g. a start-up clip: full level for crank + fire, then eases to the loop level before the hand-over
      g.gain.setValueAtTime(gain, t0 + settle.at);
      g.gain.linearRampToValueAtTime(settle.gain, t0 + settle.at + settle.over);
    }
    src.connect(g).connect(this.output);
    src.onended = () => {
      src.disconnect();
      g.disconnect();
    };
    src.start();
    return (fadeS = 0.1) => {
      const t = this.ctx.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(g.gain.value, t);
      g.gain.linearRampToValueAtTime(0, t + fadeS);
      try { src.stop(t + fadeS + 0.01); } catch { /* already stopped */ }
    };
  }
}

function nearest(rpms: number[], rpm: number): number {
  if (!rpms.length) return 0;
  let best = 0, bd = Infinity;
  rpms.forEach((r, i) => { const d = r ? Math.abs(Math.log(rpm / r)) : 0.7 + Math.random() * 0.1; if (d < bd) { bd = d; best = i; } });
  return best;
}

function pick<T>(arr: T[]): T | null {
  if (!arr.length) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}
